import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="claims-experience-selector"
export default class extends Controller {
  static targets = ["checkbox", "counter", "bulkActions", "selectAll"]
  static values = {
    storageKey: String,
    currentPage: Number
  }

  connect() {
    this.loadSelections()
    this.updateUI()

    // Listen for turbo frame load events on the specific frame
    document.addEventListener('turbo:frame-load', this.handleTurboFrameLoad.bind(this))

    // Make this controller globally accessible for the modal
    window.claimsExperienceSelector = this
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.handleTurboFrameLoad.bind(this))
  }

  selectionsObject() {
    if (!window.selections) { window.selections = {} }
    if (!window.selections.claimsExperiences) {
      window.selections.claimsExperiences = new Set()
    }

    return window.selections.claimsExperiences
  }

  // Called when any Turbo frame loads new content
  handleTurboFrameLoad(event) {
    // Check if this is our frame
    if (event.target.id === 'claims_experiences_list') {
      // Small delay to ensure DOM is fully updated
      setTimeout(() => {
        this.loadSelections()
        this.updateUI()
      }, 50)
    }
  }

  // Handle individual checkbox changes
  toggle(event) {
    const checkbox = event.target
    const id = checkbox.dataset.id

    if (checkbox.checked) {
      this.addSelection(id)
    } else {
      this.removeSelection(id)
    }

    this.updateUI()
  }

  // Handle cell clicks (clicking anywhere in the checkbox cell)
  toggleCell(event) {
    const id = event.currentTarget.dataset.id
    const checkbox = this.checkboxTargets.find(cb => cb.dataset.id === id)

    if (checkbox) {
      // Toggle the checkbox state
      checkbox.checked = !checkbox.checked

      // Update selection state
      if (checkbox.checked) {
        this.addSelection(id)
      } else {
        this.removeSelection(id)
      }

      this.updateUI()
    }
  }

  // Handle select all on current page
  selectAll(event) {
    const isChecked = event.target.checked

    this.checkboxTargets.forEach(checkbox => {
      const id = checkbox.dataset.id
      checkbox.checked = isChecked

      if (isChecked) {
        this.addSelection(id)
      } else {
        this.removeSelection(id)
      }
    })

    this.updateUI()
  }

  // Clear all selections
  clearAll() {
    this.selectionsObject().clear()
    this.loadSelections()
    this.updateUI()
  }

  // Open renewal selection modal with selected claims experiences
  openRenewalModal() {
    const selectedIds = this.getSelectedIds()

    if (selectedIds.length === 0) {
      alert("Please select at least one claims experience.")
      return
    }

    // Dispatch custom event to open modal
    const event = new CustomEvent('open-renewal-modal', {
      detail: { selectedIds }
    })
    document.dispatchEvent(event)
  }

  // Handle form submission with selected IDs
  submitWithSelected(event) {
    const selectedIds = this.getSelectedIds()

    if (selectedIds.length === 0) {
      event.preventDefault()
      alert("Please select at least one claims experience.")
      return
    }

    // Add selected IDs to form as hidden inputs
    const form = event.target.closest("form")
    if (form) {
      // Remove any existing selected_ids inputs
      form.querySelectorAll("input[name='selected_ids[]']").forEach(input => input.remove())

      // Add current selections
      selectedIds.forEach(id => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "selected_ids[]"
        input.value = id
        form.appendChild(input)
      })
    }
  }

  // Storage management
  addSelection(id) {
    console.log("Adding selection:", id)
    this.selectionsObject().add(id)
  }

  removeSelection(id) {
    this.selectionsObject().delete(id)
  }

  getSelections() {
    return this.selectionsObject()
  }

  loadSelections() {
    const selections = this.getSelections()

    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = selections.has(checkbox.dataset.id)
    })
  }

  updateUI() {
    const selections = this.getSelections()
    const count = selections.size
    const currentPageSelected = this.checkboxTargets.filter(c => c.checked).length
    const allCurrentPageSelected = currentPageSelected === this.checkboxTargets.length && this.checkboxTargets.length > 0

    // Update counter
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = count
    }

    // Show/hide bulk actions
    if (this.hasBulkActionsTarget) {
      this.bulkActionsTarget.style.display = count > 0 ? "flex" : "none"
    }

    // Update select all checkbox
    if (this.hasSelectAllTarget && this.checkboxTargets.length > 0) {
      this.selectAllTarget.checked = allCurrentPageSelected
      this.selectAllTarget.indeterminate = currentPageSelected > 0 && !allCurrentPageSelected
    }
  }

  // Get selected IDs for form submission
  getSelectedIds() {
    return [...this.getSelections()]
  }

  // Handle when checkboxes are added to the DOM (new page loaded)
  checkboxTargetConnected(checkbox) {
    // Set the state of the newly connected checkbox
    const selections = this.getSelections()
    checkbox.checked = selections.has(checkbox.dataset.id)

    // Update UI after a brief delay to ensure all targets are connected
    setTimeout(() => this.updateUI(), 10)
  }

  // Handle when checkboxes are removed from DOM (page navigation)
  checkboxTargetDisconnected() {
    // Update UI after a brief delay
    setTimeout(() => this.updateUI(), 10)
  }
}
